import { useEffect, useState, useMemo } from "react";
import styles from "./ContactForm.module.scss";
import FormTextfield from "../FormComponents/FormTextfield/FormTextfield";
import { Controller } from "react-hook-form";
import { FormHelperText } from "@mui/material";
import Select from "react-select";
import useMobile from "../../hooks/useMobile";
import CountryList from "country-list-with-dial-code-and-flag";
import usePreferencesStore from "../../store/preferencesStore";

function ContactForm(props) {
  const { control, watch, isValid, errorMobile, setErrorMobile, setMobile } =
    props;
  const isMobile = useMobile();
  const { preferences } = usePreferencesStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("+39");
  const [formState, setFormState] = useState({
    nameFirst: props.namefirst || "",
    nameLast: props.namelast || "",
    mobile: props.mobile || "",
    email: props.contactEmail || "",
    note: props.note || "",
  });

  const watchFields = watch();

  // Log form state changes
  useEffect(() => {
    console.log("Form State Updated:", {
      watchFields,
      formState,
      selectedCountry,
      fullMobile: selectedCountry + (watchFields.mobile || ""),
    });
  }, [watchFields, formState, selectedCountry]);

  const europeanCountries = [
    "Italy",
    "France",
    "Germany",
    "Spain",
    "Portugal",
    "United Kingdom",
    "Greece",
    "Netherlands",
    "Belgium",
    "Sweden",
    "Norway",
    "Denmark",
    "Finland",
    "Ireland",
    "Poland",
    "Czech Republic",
    "Austria",
    "Switzerland",
    "Hungary",
    "Romania",
    "Bulgaria",
    "Croatia",
    "Slovakia",
    "Slovenia",
    "Estonia",
    "Latvia",
    "Lithuania",
    "Iceland",
    "Luxembourg",
    "Malta",
    "Monaco",
    "Liechtenstein",
    "San Marino",
    "Andorra",
  ];

  const countries = useMemo(
    () =>
      CountryList.getAll()
        .filter((country) => europeanCountries.includes(country.name))
        .map((country) => ({
          value: country.dial_code,
          label: `${country.flag} ${country.dial_code} - ${country.name}`,
          flag: country.flag,
          dial_code: country.dial_code,
          name: country.name,
        })),
    []
  );

  const handleCountryChange = (selectedOption) => {
    const newCountry = selectedOption ? selectedOption.value : "";
    setSelectedCountry(newCountry);
    console.log("Country Changed:", {
      newCountry,
      currentMobile: watchFields.mobile,
    });
  };

  const fullMobile = useMemo(
    () => selectedCountry + (watchFields.mobile || ""),
    [selectedCountry, watchFields.mobile]
  );

  // Handle mobile number changes
  useEffect(() => {
    if (fullMobile !== props.mobile) {
      setMobile(fullMobile);
      setErrorMobile(!selectedCountry || !watchFields.mobile);
      console.log("Mobile Updated:", {
        fullMobile,
        selectedCountry,
        mobile: watchFields.mobile,
      });
    }
  }, [
    fullMobile,
    selectedCountry,
    watchFields.mobile,
    setMobile,
    setErrorMobile,
    props.mobile,
  ]);

  // Handle form field changes
  useEffect(() => {
    const newState = {
      nameFirst: watchFields.nameFirst,
      nameLast: watchFields.nameLast,
      mobile: fullMobile,
      email: watchFields.email,
      note: watchFields.note,
    };

    if (JSON.stringify(newState) !== JSON.stringify(formState)) {
      setFormState(newState);
      props.onChangeNameFirst(watchFields.nameFirst);
      props.onChangeNameLast(watchFields.nameLast);
      props.onChangePhone(fullMobile);
      props.onChangecontactEmail(watchFields.email);
      if (props.onChangeNote) {
        props.onChangeNote(watchFields.note);
      }
      console.log("Form Fields Updated:", newState);
    }
  }, [watchFields, fullMobile, props, formState]);

  // Handle form validation
  useEffect(() => {
    if (props.onChangeIsValid) {
      props.onChangeIsValid(isValid);
      console.log("Form Validation Changed:", { isValid });
    }
  }, [isValid, props]);

  const renderTextField = (name, label, autoComplete, isRequired = true) => (
    <Controller
      name={name}
      control={control}
      defaultValue={formState[name]}
      rules={{ required: isRequired }}
      render={({ field, fieldState: { error } }) => (
        <div style={{ marginBottom: error ? "20px" : "10px" }}>
          <FormTextfield
            label={label}
            type="text"
            formName={name}
            autoComplete={autoComplete}
            field={field}
            error={error}
          />
        </div>
      )}
    />
  );

  return (
    <div className={styles["contact-form-container"]}>
      <div className={styles["contact-form-title-container"]}>
        <span className={styles["contact-form-title"]}>I tuoi dati</span>
      </div>
      {!isMobile ? (
        <>
          <div className={styles["contact-form-row"]}>
            {renderTextField("nameFirst", "Nome *", "given-name")}
            {renderTextField("nameLast", "Cognome *", "family-name")}
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "20px" }}>
            <div style={{ display: "flex", gap: "20px", flex: 1 }}>
              <Select
                id="country-select"
                placeholder="Prefisso *"
                value={countries.find(
                  (option) => option.value === selectedCountry
                )}
                onChange={handleCountryChange}
                options={countries}
                onMenuOpen={() => setIsMenuOpen(true)}
                onMenuClose={() => setIsMenuOpen(false)}
                getOptionLabel={(option) =>
                  `${option.flag} ${option.dial_code} ${option.name}`
                }
                formatOptionLabel={(option) => (
                  <div>
                    {option.flag} {option.dial_code}{" "}
                    {selectedCountry !== option.dial_code || isMenuOpen
                      ? ""
                      : ""}
                  </div>
                )}
                styles={{
                  fontSize: 16,
                  valueContainer: (provided) => ({
                    ...provided,
                    fontSize: 16,
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    fontSize: 16,
                    fontFamily: "Poppins",
                  }),
                  input: (provided) => ({
                    ...provided,
                    height: 46,
                    minWidth: 80,
                    fontSize: 16,
                  }),
                  menu: (provided) => ({
                    ...provided,
                    fontSize: 14,
                    marginTop: errorMobile ? "-20px" : "-5px",
                    zIndex: "99999",
                  }),
                }}
              />
              <div style={{ flex: 1, flexDirection: "column", gap: "20px" }}>
                <Controller
                  name="mobile"
                  control={control}
                  defaultValue={props.mobile}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <div>
                      <FormTextfield
                        label="Numero di telefono *"
                        type="text"
                        formName="mobile"
                        field={field}
                        error={error}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {renderTextField(
                "email",
                `Indirizzo email ${
                  preferences.email_is_mandatory ? "*" : "(opz.)"
                }`,
                "email",
                preferences.email_is_mandatory
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {renderTextField("nameFirst", "Nome *", "given-name")}
          {renderTextField("nameLast", "Cognome *", "family-name")}
          <div style={{ display: "flex", gap: "10px", flex: 1 }}>
            <Select
              id="country-select"
              placeholder="Prefisso *"
              value={countries.find(
                (option) => option.value === selectedCountry
              )}
              onChange={handleCountryChange}
              options={countries}
              onMenuOpen={() => setIsMenuOpen(true)}
              onMenuClose={() => setIsMenuOpen(false)}
              getOptionLabel={(option) =>
                `${option.flag} ${option.dial_code} ${option.name}`
              }
              formatOptionLabel={(option) => (
                <div>
                  {option.flag} {option.dial_code}{" "}
                  {selectedCountry !== option.dial_code || isMenuOpen
                    ? option.name
                    : ""}
                </div>
              )}
              styles={{
                fontSize: 16,
                valueContainer: (provided) => ({
                  ...provided,
                  fontSize: 16,
                }),
                placeholder: (provided) => ({
                  ...provided,
                  fontSize: 16,
                  fontFamily: "Poppins",
                }),
                input: (provided) => ({
                  ...provided,
                  height: 46,
                  minWidth: 80,
                  fontSize: 16,
                }),
                menu: (provided) => ({
                  ...provided,
                  fontSize: 14,
                  marginTop: errorMobile ? "-20px" : "-5px",
                  zIndex: "99999",
                }),
              }}
            />
            <div
              style={{
                flex: 1,
                flexDirection: "column",
                gap: "20px",
                marginBottom: "10px",
              }}
            >
              <Controller
                name="mobile"
                control={control}
                defaultValue={props.mobile}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <FormTextfield
                      label="Numero di telefono"
                      type="text"
                      formName="mobile"
                      field={field}
                      error={error}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          {renderTextField(
            "email",
            `Indirizzo email ${
              preferences.email_is_mandatory ? "*" : "(opz.)"
            }`,
            "email",
            preferences.email_is_mandatory
          )}
        </>
      )}
      {props.showNote && (
        <div className={styles["contact-form-row"]}>
          <Controller
            name="note"
            control={control}
            defaultValue={props.note}
            rules={{
              required: false,
            }}
            render={({ field, fieldState: { error } }) => (
              <FormTextfield
                label="Scrivi nota (opzionale)"
                type={"text"}
                formName="note"
                field={field}
                error={error}
                multiline={true}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}

export default ContactForm;
